<template>
    <v-dialog v-model="dialog" width="500" persistent>
        <template v-slot:activator="{ on, attrs }">
            <v-list-item v-if="activatorType === 'listItem'" v-bind="attrs" v-on="on">
                <v-list-item-icon>
                    <v-icon>{{ activatorIcon }}</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="mr-5">{{ activatorLabel }}</v-list-item-title>
            </v-list-item>
            <div v-else v-bind="attrs" v-on="on" class="d-inline">
                <slot></slot>
            </div>
        </template>
        <v-card>
            <v-card-title class="flex-nowrap align-start">
                {{ title }}
                <v-spacer></v-spacer>
                <v-btn @click="dialog = false" icon><v-icon>clear</v-icon></v-btn>
            </v-card-title>
            <v-card-text class="mt-4">
                <v-form v-model="valid" ref="form">
                    <v-menu
                        v-model="startDateMenu"
                        :close-on-content-click="false"
                        :nudge-right="33"
                        :nudge-top="30"
                        transition="scale-transition"
                        offset-y
                        min-width="auto">
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                                v-model="startDate"
                                outlined
                                dense
                                label="Start date"
                                prepend-icon="mdi-calendar"
                                readonly
                                v-bind="attrs"
                                v-on="on"></v-text-field>
                        </template>
                        <v-date-picker v-model="startDate" @input="startDateMenu = false" :max="maximumStartDate"></v-date-picker>
                    </v-menu>
                    <v-menu
                        v-model="endDateMenu"
                        :close-on-content-click="false"
                        :nudge-right="33"
                        :nudge-top="30"
                        transition="scale-transition"
                        offset-y
                        min-width="auto">
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                                v-model="endDate"
                                outlined
                                dense
                                label="End date"
                                prepend-icon="mdi-calendar"
                                readonly
                                v-bind="attrs"
                                v-on="on"></v-text-field>
                        </template>
                        <v-date-picker v-model="endDate" @input="endDateMenu = false" :min="minimumEndDate"></v-date-picker>
                    </v-menu>
                    <slot name="input"></slot>
                    <v-text-field
                        v-if="limitType === 'ncu'"
                        v-model="ncuLimit"
                        v-mask="integerMask"
                        :rules="[rules.required, rules.minimumZero]"
                        prepend-icon="mdi-memory"
                        outlined
                        dense
                        label="NCU limit"></v-text-field>
                    <v-text-field
                        v-if="limitType === 'fs'"
                        v-model="fsLimit"
                        v-mask="gbMask"
                        :rules="[rules.required, rules.minimumZero]"
                        prepend-icon="mdi-file-cabinet"
                        outlined
                        dense
                        label="File system limit">
                        <template v-slot:append-outer>
                            <span class="mt-1">GB</span>
                        </template>
                    </v-text-field>
                    <v-text-field
                        v-if="limitType === 'resting'"
                        v-model="restingLimit"
                        v-mask="gbMask"
                        :rules="[rules.required, rules.minimumZero]"
                        prepend-icon="mdi-database-outline "
                        outlined
                        dense
                        label="Resting storage limit">
                        <template v-slot:append-outer>
                            <span class="mt-1">GB</span>
                        </template>
                    </v-text-field>
                </v-form>
                <v-alert type="error" text v-if="error?.code === 'ncu_limit_overlap'">
                    Overlapping NCU limit(s) found:
                    <ul>
                        <li v-for="overlap in error.overlaps" :key="overlap.nlid">
                            {{ overlap.start_date }} - {{ overlap.end_date }} with {{ overlap.limit_ncuhour }} NCU limit
                        </li>
                    </ul>
                </v-alert>
                <v-alert type="error" text v-else-if="error?.code === 'storage_limit_overlap'">
                    Overlapping File sytem limit(s) found:
                    <ul>
                        <li v-for="overlap in error.overlaps" :key="overlap.nlid">
                            {{ overlap.start_date }} - {{ overlap.end_date }} with {{ humanFileSize(overlap.limit_bytes) }} File system limit
                        </li>
                    </ul>
                </v-alert>
                <v-alert type="error" text v-else-if="error">{{ error }}</v-alert>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>

                <v-btn color="primary" text v-if="!success" :loading="sending" :disabled="!valid" @click="primaryCtaClicked()">{{ ctaLabel }}</v-btn>
                <v-btn color="success" text v-if="success"><v-icon>mdi-check</v-icon></v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { DateTime } from 'luxon'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'
import { humanFileSize } from '@/utils'

const integerMask = createNumberMask({
    prefix: ''
})

const gbMask = createNumberMask({
    prefix: '',
    allowDecimal: true,
    decimalLimit: 2
})

const defaultNow = DateTime.now().toISODate()
const defaultEnd = DateTime.now().plus({ years: 1 }).toISODate()
const defaultNcuLimit = ''
const defaultFsLimit = ''
const defaultRestingLimit = ''

export default {
    name: 'LimitDialog',
    components: {},
    props: {
        crud: { type: String, default: 'create' },
        prefill: { type: Object, default: null },
        rpid: Number,
        limitType: { type: String, default: 'ncu' }, // 'ncu', 'fs', 'resting'
        activatorType: { type: String, default: 'button' },
        activatorIcon: { type: String, default: 'mdi-plus' },
        activatorLabel: { type: String, default: '' }
    },
    data() {
        return {
            dialog: false,
            integerMask,
            gbMask,
            rules: {
                required: value => !!value || 'This field is required',
                minimumZero: value => {
                    const num = parseFloat(value)
                    return (!isNaN(num) && num >= 0) || 'Minimum value is 0'
                }
            },
            startDateMenu: false,
            startDate: defaultNow,
            endDateMenu: false,
            endDate: defaultEnd,
            ncuLimit: defaultNcuLimit,
            fsLimit: defaultFsLimit,
            restingLimit: defaultRestingLimit,
            sending: false,
            success: false,
            error: null,
            valid: false
        }
    },
    computed: {
        title() {
            switch (this.limitType) {
                case 'ncu':
                    return this.crud === 'create' ? 'New NCU limit' : 'Edit NCU limit'
                case 'fs':
                    return this.crud === 'create' ? 'New File system limit' : 'Edit File system limit'
                case 'resting':
                    return this.crud === 'create' ? 'New Resting storage limit' : 'Edit Resting storage limit'
                default:
                    return 'Limit'
            }
        },
        ctaLabel() {
            return this.crud === 'create' ? 'Create' : 'Save'
        },
        ncuLimitNumber() {
            return parseFloat(this.ncuLimit.replace(',', ''))
        },
        fsLimitNumber() {
            return parseFloat(this.fsLimit.replace(',', ''))
        },
        restingLimitNumber() {
            return parseFloat(this.restingLimit.replace(',', ''))
        },
        minimumEndDate() {
            return DateTime.fromISO(this.startDate).plus({ days: 1 }).toISODate()
        },
        maximumStartDate() {
            return DateTime.fromISO(this.endDate).minus({ days: 1 }).toISODate()
        }
    },
    methods: {
        async primaryCtaClicked() {
            this.error = null
            this.sending = true
            try {
                const params = {
                    new_start_date: this.startDate,
                    new_end_date: this.endDate
                }

                let url = ''
                let id = ''
                let method = ''

                switch (this.limitType) {
                    case 'ncu':
                        params.new_ncu_limit = this.ncuLimitNumber
                        url = `/resource/${this.rpid}/ncul`
                        id = this.prefill?.nlid
                        break
                    case 'fs':
                        params.new_storage_limit = this.fsLimitNumber * (1000 * 1000 * 1000)
                        url = `/resource/${this.rpid}/fsl`
                        id = this.prefill?.fslid
                        break
                    case 'resting':
                        params.new_storage_limit = this.restingLimitNumber * (1000 * 1000 * 1000)
                        url = `/resource/${this.rpid}/rsl`
                        id = this.prefill?.rslid
                        break
                }

                if (this.crud === 'create') {
                    method = 'post'
                } else {
                    method = 'patch'
                    url += `/${id}`
                }

                await this.$axios[method](url, params)

                this.success = true
                this.$emit('success')
                setTimeout(() => {
                    this.dialog = false
                }, 2000)
            } catch (error) {
                if (error?.response?.data?.code === 'ncu_limit_overlap' || error?.response?.data?.code === 'storage_limit_overlap') {
                    this.error = error.response.data
                } else {
                    this.error = error.message || 'An error occurred while processing your request.'
                }
            }
            this.sending = false
        },
        humanFileSize(bytes, si) {
            return humanFileSize(bytes, si)
        }
    },
    watch: {
        dialog: {
            handler(open) {
                if (open) {
                    if (this.crud === 'create') {
                        this.startDate = defaultNow
                        this.endDate = defaultEnd
                        this.ncuLimit = defaultNcuLimit
                        this.fsLimit = defaultFsLimit
                        this.restingLimit = defaultFsLimit
                    } else {
                        this.startDate = this.prefill.start_date
                        this.endDate = this.prefill.end_date
                        this.ncuLimit = this.prefill.limit_ncuhour
                        this.fsLimit = (this.prefill.limit_bytes / (1000 * 1000 * 1000)).toFixed(2)
                        this.restingLimit = (this.prefill.limit_bytes / (1000 * 1000 * 1000)).toFixed(2)
                    }
                    this.success = false
                    this.error = null
                }
            },
            immediate: true
        }
    },
    mounted() {}
}
</script>
